import React, { useState } from 'react';
import { useResponsive } from '../hooks/useResponsive';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  onConfirm
}) => {
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await onConfirm();
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '10px' : '20px'
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: isMobile ? '12px' : '16px',
        maxWidth: isMobile ? 'calc(100vw - 20px)' : '500px',
        width: '100%',
        border: '1px solid #33383B',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          padding: isMobile ? '16px' : '20px',
          borderBottom: '2px solid #EF4444',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#EF4444"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <h2 style={{
            margin: 0,
            color: '#EF4444',
            fontSize: isMobile ? '18px' : '22px',
            fontWeight: '700',
            letterSpacing: '0.5px'
          }}>
            ELIMINAR CUENTA
          </h2>
        </div>

        {/* Content */}
        <div style={{
          padding: isMobile ? '20px' : '28px'
        }}>
          <p style={{
            margin: '0 0 20px 0',
            color: '#E5E7EB',
            fontSize: isMobile ? '14px' : '16px',
            lineHeight: '1.6'
          }}>
            Estás por eliminar tu cuenta. Esta acción:
          </p>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#10B981"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0, marginTop: '2px' }}
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <p style={{
                margin: 0,
                color: '#D1D5DB',
                fontSize: isMobile ? '13px' : '15px',
                lineHeight: '1.5'
              }}>
                Eliminará tu email y datos personales
              </p>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#10B981"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0, marginTop: '2px' }}
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <p style={{
                margin: 0,
                color: '#D1D5DB',
                fontSize: isMobile ? '13px' : '15px',
                lineHeight: '1.5'
              }}>
                Mantendrá tu historial de partidos y reviews
              </p>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#10B981"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0, marginTop: '2px' }}
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <p style={{
                margin: 0,
                color: '#D1D5DB',
                fontSize: isMobile ? '13px' : '15px',
                lineHeight: '1.5'
              }}>
                Transferirá tus partidos programados a otro jugador
              </p>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#EF4444"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0, marginTop: '2px' }}
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              <p style={{
                margin: 0,
                color: '#D1D5DB',
                fontSize: isMobile ? '13px' : '15px',
                lineHeight: '1.5'
              }}>
                NO podrás iniciar sesión nuevamente
              </p>
            </div>
          </div>

          {/* Warning Box */}
          <div style={{
            backgroundColor: 'rgba(245, 158, 11, 0.15)',
            border: '2px solid #F59E0B',
            borderRadius: '8px',
            padding: isMobile ? '12px' : '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#F59E0B"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p style={{
              margin: 0,
              color: '#F59E0B',
              fontSize: isMobile ? '13px' : '15px',
              fontWeight: '600'
            }}>
              Esta acción NO es reversible
            </p>
          </div>

          {/* Actions */}
          <div style={{
            display: 'flex',
            gap: '12px',
            flexDirection: isMobile ? 'column-reverse' : 'row'
          }}>
            <button
              onClick={onClose}
              disabled={loading}
              style={{
                flex: 1,
                padding: isMobile ? '12px' : '14px 24px',
                backgroundColor: '#2C3135',
                color: '#E5E7EB',
                border: '1px solid #4B5563',
                borderRadius: '8px',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = '#374151';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2C3135';
              }}
            >
              CANCELAR
            </button>

            <button
              onClick={handleConfirm}
              disabled={loading}
              style={{
                flex: 1,
                padding: isMobile ? '12px' : '14px 24px',
                backgroundColor: '#EF4444',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = '#DC2626';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#EF4444';
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #FFFFFF',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  <span>ELIMINANDO...</span>
                </>
              ) : (
                'ELIMINAR CUENTA'
              )}
            </button>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};
